import React from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '@bumble/app/components/Loading';

interface UserAgentUnlinkProps {
  onSubmit: () => void;
  userAgentId: string;
}
export const UserAgentUnlink = ({ onSubmit, userAgentId }: UserAgentUnlinkProps) => {
  const api = useApi();

  const { isLoading, data } = useQuery(
    ['userAgentsLinks'],
    api.userAgentGetLinkedEpApiV1UserAgentGetLinkedEpGet.bind(null, {
      user_agent_id: userAgentId,
    }),
    { enabled: true },
  );

  const [endpoint, setEndpoint] = React.useState<string>('');

  const handleSetEndpoint = (event: SelectChangeEvent) => {
    setEndpoint(event.target.value as string);
  };

  const unlinkUserAgent = useMutation(api.unlinkUserAgentFromEpApiV1UnlinkUserAgentFromEpPost, {
    onSuccess() {
      toast.success(`User agent ${userAgentId} successfully unlinked from endpoint ${endpoint}.`);
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    unlinkUserAgent.mutate({
      user_agent_id: userAgentId,
      ep_id: endpoint,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box pt={2}>
      {!isLoading && data?.data && data?.data.length > 0 ? (
        <FormControl fullWidth>
          <InputLabel id="endpoint-label">Endpoint(s)</InputLabel>
          <Select
            labelId="endpoint-label"
            id="endpoint"
            label="Endpoint(s)"
            value={endpoint}
            onChange={handleSetEndpoint}
          >
            {data?.data.map((ep) => (
              <MenuItem value={ep} key={ep}>
                {ep}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Alert severity="error">User agent has no endpoints linked.</Alert>
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={endpoint === '' || !endpoint}>
          Unlink User Agent
        </Button>
      </Box>
    </Box>
  );
};
UserAgentUnlink.displayName = 'UserAgentUnlink';
